import { FormInstance } from "antd";
import { useCallback, useState } from "react";
import { useFormData } from "./useFormData";
import MailingsService from "../../../services/mailings";
import { useAppDispatch } from "../../../store";
import { rescheduleEmail } from "../../../acions/emails";
import { setErrorMessage, setSuccessMessage } from "../../../acions/actionsCreators/messageActions";
import { EMAILS_WERE_SENT } from "../../../lib/constants";
import { useGetSendTaskStatus } from "./useGetSendTaskStatus";

export const useSave = (
  id: string | null,
  form: FormInstance,
  mailingType: 1 | 2 | 3,
  sendToData: any,
  closeModal: () => void,
  reloadData: () => void,
  setIsSaving: React.Dispatch<React.SetStateAction<any>>
) => {
  const dispatch = useAppDispatch();

  const [initialFiles, setInitialFiles] = useState<string[]>([]);

  const { getFormData } = useFormData(sendToData, initialFiles);

  const onSuccess = useCallback(() => {
    reloadData();
    closeModal();
    setIsSaving(false);
  }, [closeModal, reloadData, setIsSaving]);

  const onTaskComplete = useCallback(() => {
    dispatch(setSuccessMessage(EMAILS_WERE_SENT));
    onSuccess();
  }, [dispatch, onSuccess]);

  const onError = useCallback(
    (e: any) => {
      setIsSaving(false);
      dispatch(setErrorMessage(e.response.data.title, true));
    },
    [dispatch, setIsSaving]
  );

  const { getStatus } = useGetSendTaskStatus(onTaskComplete, onError, false);

  const onSave = useCallback(async () => {
    try {
      if (id) {
        const values = await form.validateFields();

        setIsSaving(true);

        const formData = getFormData(values);

        const taskId: string = await MailingsService.sendandsend(formData, id, mailingType);

        await getStatus(taskId);
      }
    } catch (e: any) {
      e.response && onError(e);
    }
  }, [id, setIsSaving, form, getFormData, mailingType, onError, getStatus]);

  const resend = useCallback(async () => {
    try {
      setIsSaving(true);

      const taskId: any = await MailingsService.resend(id || "");

      await getStatus(taskId);
    } catch (e) {
      onError(e);
    }
  }, [id, setIsSaving, getStatus, onError]);

  const reschedule = useCallback(
    async (scheduleValues: any) => {
      try {
        if (id) {
          const values = await form.validateFields();

          setIsSaving(true);

          const formData = getFormData(values, scheduleValues);

          await Promise.resolve(dispatch(rescheduleEmail(id, formData)));

          onSuccess();
        }
      } catch {
        setIsSaving(false);
      }
    },
    [id, form, setIsSaving, getFormData, dispatch, onSuccess]
  );

  return { onSave, setInitialFiles, reschedule, resend };
};
